import { useState, useEffect, useCallback } from "react";
import { api } from "../api";
import { PortalLayout } from "../components/Portal";
import { Card, Select, Field, Btn, Spinner, cellClip } from "../components/Shared";
import { BarChart, DonutChart, StackedBars, SERIES_COLOURS } from "../components/Charts";
import { documentWord, STATEMENT_OF_WORK, useDocumentWord } from "../documentWord";

/**
 * How the work went.
 *
 * The server decides what this caller may be told about — facility, group, or
 * their own projects — so the page renders whatever it is given rather than
 * asking for a scope. An operator who asks for the group's figures gets theirs,
 * which is the honest answer rather than a dead end.
 *
 * Every number on screen comes from the API. Nothing here adds anything up: the
 * export and the page have to agree, and a page that computed its own totals is
 * how they stop agreeing.
 */
const OUTCOME_LABELS = {
  running: "Running", finished: "Finished", cancelled: "Cancelled",
  expired: "Expired", unstated: "No reason recorded",
};

// Green for delivered, red for called off, amber for lapsed — and the palette
// is the colour-blind-safe one, so the ordering is not the only cue.
const OUTCOME_COLOURS = {
  running: SERIES_COLOURS[0], finished: SERIES_COLOURS[1], cancelled: SERIES_COLOURS[2],
  expired: SERIES_COLOURS[4], unstated: "#9ca3af",
};

const SCOPE_WORDS = {
  facility: "every group in the facility",
  group: "this group",
  own: "the projects you run",
};

const th = { padding: "8px 12px", textAlign: "left", fontSize: 12, fontWeight: 600,
             color: "#6b7280", textTransform: "uppercase", letterSpacing: 0.4 };
const td = { padding: "8px 12px", fontSize: 13, color: "#374151", ...cellClip };

export default function StatsPage() {
  const word = useDocumentWord();
  const [year, setYear] = useState(null);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  // Kept apart from `error` above, which REPLACES the page: a failed export
  // must not throw away the statistics that did load.
  const [exportError, setExportError] = useState(null);

  const load = useCallback(() => {
    api.get(`/api/stats${year ? `?year=${year}` : ""}`)
      .then(d => { setData(d); setError(null); if (year === null) setYear(d.year); })
      .catch(e => setError(e.message));
  }, [year]);

  useEffect(load, [load]);

  if (error) {
    return <PortalLayout><Card><div role="alert" style={{ color: "#dc2626" }}>{error}</div></Card></PortalLayout>;
  }
  if (!data) return <PortalLayout><Card><Spinner /> Loading…</Card></PortalLayout>;

  const outcomes = Object.keys(OUTCOME_LABELS).map(k => ({
    label: OUTCOME_LABELS[k], value: data.outcomes[k] || 0, colour: OUTCOME_COLOURS[k],
  }));

  // **One block per currency, never one number over both.** The currency is
  // stamped per quotation from the group, `default_currency` is editable free
  // text and a project can be moved between groups — so a facility holding two
  // is legitimate, and a single total over them would be a figure in no
  // currency at all. The bars are built from what the API sent and never summed
  // here: these are strings on purpose, and adding them in the browser would
  // break the contract they exist to hold.
  const byCurrency = (data.money || []).map(row => ({
    currency: row.currency,
    discounted: row.discounted,
    bars: [
      { label: "Delivered", value: Number(row.delivered), colour: SERIES_COLOURS[1] },
      { label: "Lapsed", value: Number(row.lapsed), colour: SERIES_COLOURS[2] },
    ],
  }));

  const showQuotations = !word.sow || (data?.issued?.quotations || 0) > 0;

  return (
    <PortalLayout>
      <div style={{ display: "flex", alignItems: "flex-end", gap: 16, flexWrap: "wrap",
                    marginBottom: 24 }}>
        <div>
          <h1 style={{ margin: "0 0 4px", fontSize: 26, fontWeight: 700 }}>Statistics</h1>
          <p style={{ margin: 0, color: "#6b7280", fontSize: 14 }}>
            {data.group_name ? `${data.group_name} — ` : ""}
            {SCOPE_WORDS[data.scope]}, {data.year}.
          </p>
        </div>
        <div style={{ marginLeft: "auto", display: "flex", gap: 12, alignItems: "flex-end" }}>
          <div style={{ minWidth: 120 }}>
            <Field label="Year">
              <Select
                options={data.years.map(y => ({ value: String(y), label: String(y) }))}
                value={String(year ?? data.year)}
                onChange={e => setYear(Number(e.target.value))}
              />
            </Field>
          </div>
          {/* Fetched with the session token and handed to the browser as a
              blob. A plain navigation cannot carry the header — this used to
              say the opposite and downloaded a JSON refusal instead. */}
          <Btn onClick={async () => {
            setExportError(null);
            try {
              await api.saveFile(`/api/stats/export.xlsx?year=${year ?? data.year}`);
            } catch (err) {
              setExportError(err.message || "The export could not be downloaded.");
            }
          }}>
            Download Excel
          </Btn>
          {exportError && (
            <div role="alert" style={{ marginTop: 8, color: "#b91c1c", fontSize: 13 }}>
              {exportError}
            </div>
          )}
        </div>
      </div>

      <Card>
        <DonutChart data={outcomes} title={`${data.project_count} project(s) in ${data.year}`} />
        {/* What "project" counts, which depends on whose report this is
            (2026-11-03): for a group or an operator the projects they ISSUED a
            quotation or Statement of Work for — a project two groups worked on
            counts for both, and owning one without issuing counts for neither. */}
        {data.counts_basis && (
          <p data-counts-basis style={{ margin: "8px 0 0", fontSize: 13, color: "#6b7280" }}>
            Counted: {data.counts_basis}.
          </p>
        )}
      </Card>

      {data.issued && (
        <>
          <div style={{ height: 20 }} />
          <Card>
            <h3 style={{ margin: "0 0 4px", fontSize: 16, fontWeight: 700 }}>Issued in {data.year}</h3>
            <p style={{ margin: "0 0 12px", fontSize: 13, color: "#6b7280" }}>
              Created in the year, by the person who created each one and under the group that
              issued it. Ten on one project are one project and ten documents. A deleted
              project still counts — its figures are kept when it goes.
            </p>
            {/* A portal issuing Statements of Work never says "quotation"
                unless one was really issued in the year (2026-11-03). */}
            <p data-issued-total style={{ margin: "0 0 16px", fontSize: 14 }}>
              {showQuotations && (
                <><strong>{data.issued.quotations}</strong> {documentWord("quotation", { plural: true })},{" "}</>
              )}
              <strong>{data.issued.statements_of_work}</strong>{" "}
              {documentWord(STATEMENT_OF_WORK, { plural: true })}, on{" "}
              <strong>{data.issued.projects}</strong> project(s).
            </p>
            <h4 style={{ margin: "0 0 6px", fontSize: 14, fontWeight: 600 }}>By operator</h4>
            <Table
              headers={["Operator", "Projects", ...(showQuotations ? [documentWord("quotation", { plural: true, capital: true })] : []),
                        documentWord(STATEMENT_OF_WORK, { plural: true, capital: true })]}
              rows={issuedRows(data.by_operator, "operator").map(r => [
                r.operator, r.projects, ...(showQuotations ? [r.quotations] : []), r.statements_of_work])}
              empty="Nothing was issued in this period."
            />
            {(data.by_group || []).length > 0 && (
              <>
                <h4 style={{ margin: "16px 0 6px", fontSize: 14, fontWeight: 600 }}>By issuing group</h4>
                <Table
                  headers={["Group", "Projects", ...(showQuotations ? [documentWord("quotation", { plural: true, capital: true })] : []),
                            documentWord(STATEMENT_OF_WORK, { plural: true, capital: true })]}
                  rows={issuedRows(data.by_group, "group").map(r => [
                    r.group, r.projects, ...(showQuotations ? [r.quotations] : []), r.statements_of_work])}
                  empty="Nothing was issued in this period."
                />
              </>
            )}
          </Card>
        </>
      )}

      <div style={{ height: 20 }} />

      <Card>
        <h3 style={{ margin: "0 0 4px", fontSize: 16, fontWeight: 700 }}>Money</h3>
        <p style={{ margin: "0 0 8px", fontSize: 13, color: "#6b7280" }}>
          Every {word()} that <strong>ended</strong> in {data.year}, counted on the day it
          ended and at the price its signatories agreed.{" "}
          <strong>Work still running is not in these figures at all</strong> — it has not
          ended, so it belongs to no year yet.{" "}
          <strong>Delivered</strong> is work that ran to completion; <strong>Lapsed</strong>{" "}
          is work that was agreed and then cancelled or expired, the figure that is invisible
          everywhere else.
        </p>
        <p style={{ margin: "0 0 16px", fontSize: 13, color: "#6b7280" }}>
          {data.provisional ? (
            <>
              <strong>{data.year} is not closed yet</strong>, so these figures can still
              change: a {word()} that ends tomorrow joins them. Closing the year in
              Administration fixes the date they are reported to.
            </>
          ) : (
            <>
              Reported to <strong>{data.closing_date}</strong>, the date {data.year} was
              closed on. A {word()} approved after it belongs to the next year.
            </>
          )}
        </p>
        {byCurrency.length === 0 && (
          <p style={{ margin: 0, fontSize: 13, color: "#6b7280" }}>
            Nothing ended in {data.year}.
          </p>
        )}
        {byCurrency.map(row => (
          <div key={row.currency} style={{ marginBottom: 16 }}>
            {byCurrency.length > 1 && (
              <h4 style={{ margin: "0 0 6px", fontSize: 14, fontWeight: 600 }}>
                {row.currency}
              </h4>
            )}
            <BarChart data={row.bars} valueLabel={row.currency} />
            {/* Deliberately NOT a third bar. The two above are slices of the
                same total; this is the reduction already taken off them, so a
                bar beside them would read as money that exists somewhere. */}
            {Number(row.discounted) > 0 && (
              <p style={{ margin: "12px 0 0", fontSize: 13, color: "#6b7280" }}>
                <strong>{row.discounted} {row.currency}</strong> was discounted on those
                offers — what the facility chose not to charge. The figures above are already
                net of it.
              </p>
            )}
          </div>
        ))}
      </Card>

      <div style={{ height: 20 }} />

      <Card>
        <h3 style={{ margin: "0 0 12px", fontSize: 16, fontWeight: 700 }}>By operator</h3>
        {/* The slices sum to the totals above — there is no operator share, this
            is a cut of the same money. */}
        {/* A row per operator per currency, and the label carries the currency
            when there is more than one: the money sums down the page per
            currency, the project count does not — a project quoted in two
            currencies is still one project. */}
        <StackedBars
          data={data.by_operator.map(r => ({
            label: r.currency
              ? `${r.operator} (${r.projects})${
                  byCurrency.length > 1 ? ` · ${r.currency}` : ""}`
              : `${r.operator} (${r.projects})`,
            delivered: Number(r.delivered || 0),
            lapsed: Number(r.lapsed || 0),
          }))}
          keys={[
            { key: "delivered", label: "Delivered", colour: SERIES_COLOURS[1] },
            { key: "lapsed", label: "Lapsed", colour: SERIES_COLOURS[2] },
          ]}
        />
      </Card>

      <div style={{ height: 20 }} />

      <Card>
        <h3 style={{ margin: "0 0 12px", fontSize: 16, fontWeight: 700 }}>Services</h3>
        <Table
          headers={["Service", "Ordered", "Delivered", "Not delivered"]}
          rows={data.services.map(r => [r.service, r.ordered, r.delivered, r.unfinished])}
          empty={word.sow ? `No services were in a ${word()} in this period.`
                          : "No services were quoted in this period."}
        />
      </Card>

      <div style={{ height: 20 }} />

      <Card>
        <h3 style={{ margin: "0 0 12px", fontSize: 16, fontWeight: 700 }}>By affiliation</h3>
        {/* An affiliation that took work on but ended none in the year has no
            currency and no figure, and the cell stays EMPTY rather than showing
            0.00 — an absent figure and a figure of nothing are different facts,
            and a zero here reads as work done for free. */}
        <Table
          headers={["Affiliation", "Projects", "Currency", "Delivered"]}
          rows={data.by_affiliation.map(r => [
            r.affiliation, r.projects, r.currency || "—",
            r.delivered == null ? "—" : r.delivered,
          ])}
          empty="No projects in this period."
        />
      </Card>

      <div style={{ height: 20 }} />

      <Card>
        <h3 style={{ margin: "0 0 4px", fontSize: 16, fontWeight: 700 }}>Reopened</h3>
        <p style={{ margin: "0 0 12px", fontSize: 13, color: "#6b7280" }}>
          Projects taken back out of the archive, and by whom — read from the log, which is the
          only place that records it.
        </p>
        <Table
          headers={["Project", "Times", "Operator", "Reopened by"]}
          rows={data.reopened.map(r => [r.project, r.times, r.operator, r.by.join(", ")])}
          empty="Nothing was reopened."
        />
      </Card>
    </PortalLayout>
  );
}

// One row per name: the breakdowns repeat a name on each of its currency rows,
// and the issued counts are the name's, not the currency's.
function issuedRows(rows, key) {
  const seen = new Map();
  for (const r of rows || []) if (!seen.has(r[key])) seen.set(r[key], r);
  return [...seen.values()].filter(r => (r.quotations || 0) + (r.statements_of_work || 0) > 0);
}

function Table({ headers, rows, empty }) {
  if (rows.length === 0) {
    return <p style={{ fontSize: 13, color: "#6b7280", margin: 0 }}>{empty}</p>;
  }
  return (
    <div style={{ overflowX: "auto" }}>
      <table className="responsive-table"
             style={{ width: "100%", borderCollapse: "collapse", tableLayout: "fixed" }}>
        <thead>
          <tr style={{ borderBottom: "1px solid #e5e7eb" }}>
            {headers.map(h => <th key={h} style={th}>{h}</th>)}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={i} style={{ borderBottom: "1px solid #f3f4f6" }}>
              {row.map((cell, j) => (
                <td key={j} style={td} data-label={headers[j]} title={String(cell)}>{cell}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
